import keyCodes from '../../../../../../editJournal/constants/keyCodes';
import renderDict from './renderDict';
import tabHandler from '../../../../../../editJournal/slateUtilities/plugins/tab';
import getSiblingEditors, { TabRestrictedInput } from './getSiblingEditors';
import expandOrgAndRole from './expandOrgAndRole';
import { fnBrowserDetect } from '../../../../../../../../../utility/browsers';
import { SAFARI } from '../../../../../../../../../utility/enums/browsers';

export default (params) => {
  const { event, editor, dateRef } = params;
  if (event.which === keyCodes.TAB) {
    const {
      value: {
        focusBlock,
        selection: { isFocused },
        document,
      },
    } = editor;
    const { key: focusBlockKey, type: focusBlockType } = focusBlock;
    const parent = document.getParent(focusBlockKey);
    const { type: parentType } = parent;

    if (isFocused) {
      event.preventDefault();
      if (dateRef) {
        const focusBlockDate = dateRef[focusBlock.key];
        if (focusBlockDate) {
          const { current } = dateRef[focusBlock.key];
          current.setOpen(false);
        }
      }
      if (renderDict[parentType] === 'list_item') {
        const isReturn = tabHandler(event, editor, null);
        if (isReturn) return true;
      }

      /**
       * When user presses shift+tab
       */
      if (event.shiftKey) {
        let previousBlock = document.getPreviousBlock(focusBlockKey);
        if (previousBlock && dateRef) {
          const previousBlockDate = dateRef[previousBlock.key];
          if (previousBlockDate) {
            const { current } = dateRef[previousBlock.key];
            if (!current.state.open) {
              current.setOpen(true);
            }
          }
        }
        /**
         * Since we are using empty paragraphs to insert new roles/organizations/array_items,
         * the tab would see it as an focusable element
         *
         * So, unto we find the editable node where user can write the data, we need
         * to iterate through it and focus it
         */
        if (
          previousBlock &&
          (previousBlock.type === 'empty_paragraph' ||
            renderDict[previousBlock.type] === 'organization_image')
        ) {
          while (
            previousBlock &&
            (previousBlock.type === 'empty_paragraph' ||
              renderDict[previousBlock.type] === 'organization_image')
          ) {
            previousBlock = document.getPreviousBlock(previousBlock.key);
          }
        }
        if (!previousBlock) {
          const { editorRefs, formatType } = params;
          let supposedFocusBlockType = focusBlockType;
          if (focusBlockType === 'paragraph') {
            supposedFocusBlockType = parentType;
          }
          if (editorRefs) {
            const previousEditorKey =
              getSiblingEditors[formatType][supposedFocusBlockType].prev;
            const isRestrictTab =
              fnBrowserDetect() === SAFARI &&
              TabRestrictedInput.includes(previousEditorKey);

            if (previousEditorKey && !isRestrictTab) {
              const previousEditorRef = editorRefs[previousEditorKey];
              const {
                value: { document: prevEditorDocument },
              } = previousEditorRef;
              const {
                nodes: { size },
              } = prevEditorDocument;
              let lastBlockIndex = size - 1;
              if (size === 1) {
                lastBlockIndex = 0;
              }
              const lastBlock =
                prevEditorDocument.getBlocksAsArray()[lastBlockIndex];
              if (dateRef) {
                const nextEditorDate = dateRef[lastBlock.key];
                if (nextEditorDate) {
                  const { current } = dateRef[lastBlock.key];
                  if (!current.state.open) {
                    current.setOpen(true);
                  }
                }
              }
              previousEditorRef.moveToRangeOfNode(lastBlock).focus();
              return true;
            }
          }
        }
        /**
         * When there is next block to be focused,
         * we need to collapse the cursor at end
         */
        if (!previousBlock) {
          editor.moveToStartOfNode(focusBlock);
          return true;
        }
        if (focusBlock.type === 'paragraph') {
          const paragraphs = document.getParent(focusBlock.key);
          previousBlock = document.getPreviousBlock(paragraphs.key);
          if (previousBlock && dateRef) {
            const previousBlockDate = dateRef[previousBlock.key];
            if (previousBlockDate) {
              const { current } = dateRef[previousBlock.key];
              if (!current.state.open) {
                current.setOpen(true);
              }
            }
          }
        }

        if (previousBlock && previousBlock.type === 'paragraph') {
          let paragraphs = document.getParent(previousBlock.key);
          const bulletList = document.getParent(previousBlock.key);
          if (bulletList && bulletList.type === 'list_item') {
            const BulletUL = document.getParent(bulletList.key);
            const BulletULParent = document.getParent(BulletUL.key);
            paragraphs = BulletULParent;
          }

          expandOrgAndRole({ editor, focusableBlock: paragraphs });
          editor.moveToRangeOfNode(paragraphs);
          return true;
        }
        expandOrgAndRole({ editor, focusableBlock: previousBlock });

        editor.moveToRangeOfNode(previousBlock);
        return true;
      }

      let nextBlock = document.getNextBlock(focusBlockKey);
      if (nextBlock && dateRef) {
        const nextBlockDate = dateRef[nextBlock.key];
        if (nextBlockDate) {
          const { current } = dateRef[nextBlock.key];
          if (!current.state.open) {
            current.setOpen(true);
            editor.moveToStartOfNode(nextBlock).select({ isFocused: true });
            return true;
          }
        }
      }

      /**
       * Since we are using empty paragraphs to insert new roles/organizations/array_items,
       * the tab would see it as an focusable element
       *
       * So, unto we find the editable node where user can write the data, we need
       * to iterate through it and focus it
       */
      if (nextBlock && nextBlock.type === 'empty_paragraph') {
        while (nextBlock && nextBlock.type === 'empty_paragraph') {
          nextBlock = document.getNextBlock(nextBlock.key);
        }
      }

      if (!nextBlock) {
        const { editorRefs, formatType } = params;
        let supposedFocusBlockType = focusBlockType;
        if (focusBlockType === 'paragraph') {
          supposedFocusBlockType = parentType;
        }
        if (editorRefs) {
          const nextEditorKey =
            getSiblingEditors[formatType][supposedFocusBlockType].next;
          // Step 2: Focus the first focusable node that you find
          const isRestrictTab =
            fnBrowserDetect() === SAFARI &&
            TabRestrictedInput.includes(nextEditorKey);

          if (nextEditorKey && !isRestrictTab) {
            const nextEditorRef = editorRefs[nextEditorKey];
            const {
              value: { document: nextEditorDocument },
            } = nextEditorRef;
            const nextEditorFocusBlock =
              nextEditorDocument.getBlocksAsArray()[0];

            if (nextEditorFocusBlock) {
              if (dateRef) {
                const nextEditorDate = dateRef[nextEditorFocusBlock.key];
                if (nextEditorDate) {
                  const { current } = dateRef[nextEditorFocusBlock.key];
                  if (!current.state.open) {
                    current.setOpen(true);
                  }
                }
              }
            }
            nextEditorRef.moveToRangeOfNode(nextEditorFocusBlock).focus();
            return true;
          }
        }
      }

      if (nextBlock && renderDict[nextBlock.type] === 'organization_image') {
        nextBlock = editor.value.document.getNextBlock(nextBlock.key);
      }
      /**
       * When there is no next block we need to shift
       * the focus to end of the focus block
       */
      if (!nextBlock) {
        editor.moveToEndOfNode(focusBlock);
        return true;
      }

      if (focusBlock.type === 'paragraph') {
        let paragraphs = document.getParent(focusBlock.key);
        let tempkey = paragraphs.key;
        while (
          document.getParent(tempkey).type === 'list_item' ||
          document.getParent(tempkey).type === 'unordered_list'
        ) {
          tempkey = document.getParent(tempkey).key;
          paragraphs = document.getParent(tempkey);
        }
        nextBlock = document.getNextBlock(paragraphs.key);
        if (
          nextBlock &&
          (nextBlock.type === 'empty_paragraph' ||
            renderDict[nextBlock.type] === 'organization_image')
        ) {
          while (
            nextBlock &&
            (nextBlock.type === 'empty_paragraph' ||
              renderDict[nextBlock.type] === 'organization_image')
          ) {
            nextBlock = document.getNextBlock(nextBlock.key);
          }
        }
      }

      if (nextBlock && nextBlock.type === 'paragraph') {
        let paragraphsRole = document.getParent(nextBlock.key);
        const bulletList = document.getParent(nextBlock.key);
        if (bulletList && bulletList.type === 'list_item') {
          const BulletUL = document.getParent(bulletList.key);
          const BulletULParent = document.getParent(BulletUL.key);
          paragraphsRole = BulletULParent;
        }
        editor.moveToRangeOfNode(paragraphsRole);
        return true;
      }
      expandOrgAndRole({ editor, focusableBlock: nextBlock });
      editor.moveToRangeOfNode(nextBlock);
      return true;
    }
  }

  return false;
};
